import { LoadingButton } from "@mui/lab";
import { Card, CardContent, CardHeader, Divider, Grid, TextField, Typography } from "@mui/material";
import { cloneDeep, get, isString } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import OrderService from "src/_services/orderService";
import SearchIcon from '@mui/icons-material/Search';
import { MapItTextField } from "src/components/common";
import { MapitShipCitiesAutocomplete } from "src/_modules/MapitShip/components";
import { useGetAllCitiesQuery } from "src/_api/mapitShipApi";
import { sanitizeShortAddress } from "src/_utils";

const BasicFields = [
    { name: "state", label: "State/Region", size: "small", maxLength: 40, xs: 12, md: 6 },
    { name: "city", label: "City", size: "small", required: true, maxLength: 500, xs: 12, md: 6 },
    { name: "area", label: "District", size: "small", required: true, maxLength: 50, xs: 12, md: 6 },
    { name: "street", label: "Street", size: "small", required: true, maxLength: 450, required: true, xs: 12, md: 6 },
    { name: "houseNumber", label: "Building No.", size: "small", required: false, maxLength: 100, xs: 12, md: 6 },
    { name: "postcode", label: "Postcode", size: "small", required: false, maxLength: 30, xs: 12, md: 6 },
    { name: "landmark", label: "Details", size: "small", required: false, placeholder: "addressDetailsPlaceholder", maxLength: 350, xs: 12, md: 12 },
]

export default function AddressDetails({
    address,
    validationErrors,
    handleChange,
    CustomFields,
    nationalAddressRequired = false,
    cardTitle = "Address Info",
    hideLandmark = false,
    disableNationalAddress = false
}) {
    const { t } = useTranslation();
    const [nationalAddress, setNationalAddress] = useState('')
    const [loading, setLoading] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [error, setError] = useState(null)
    const [validateNationalNumber, setValidateNationalNumber] = useState(null)

    const Fields = useMemo(() => {
        let fields = CustomFields ?? BasicFields
        return fields.filter(field => !(field.name === "landmark" && hideLandmark))
    }, [CustomFields, hideLandmark])

    const { data: cities, isLoading } = useGetAllCitiesQuery()

    // Local check (Search button) first, then the submit-time schema error for the same field
    const shortAddressError = validateNationalNumber ?? get(validationErrors, "shortAddress")

    useEffect(() => {
        const sanitized = sanitizeShortAddress(address?.shortAddress) ?? ''
        setNationalAddress(sanitized)
        // Clean the value stored in the form too, so the submitted payload never carries spaces
        if (!!address?.shortAddress && address.shortAddress !== sanitized) {
            handleChange({
                target: {
                    name: "address",
                    value: { ...address, shortAddress: sanitized },
                },
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [address?.shortAddress])

    const getAddressFromNationalAddress = async () => {
        try {
            setIsSuccess(false)
            setError(null)
            setValidateNationalNumber(null)
            setLoading(true)
            const code = sanitizeShortAddress(nationalAddress)
            setNationalAddress(code)
            const valid = /^[A-Z]{4}[0-9]{4}/.test(code)
            if (!valid || code.length !== 8) {
                setValidateNationalNumber('Please make sure you entered a valid national number')
                return
            }
            let response = await OrderService.getAddressFromNationalAddress({ nationalAddress: code })
            let data = cloneDeep(response.data.body)
            let address = data?.address

            const isArabic = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(address?.city ?? '')

            let city = isArabic ? '' : address?.city

            if (isArabic) {
                city = (cities ?? []).find((el) => el?.name_ar === address?.city)?.name_en ?? ""
            }

            let addr = {
                city: city,
                state: "",
                area: address?.area,
                street: address?.street,
                houseNumber: address?.houseNumber,
                landmark: "",
                postcode: address?.postcode,
                shortAddress: code
            }

            handleChange({ target: { name: "address", value: addr } })
            handleChange({ target: { name: "location.coordinates", value: data?.location.map(coord => Number(coord)) } })
        } catch (err) {
            let message = err?.response?.data?.message ?? 'Something went wrong please try again'
            setError(message)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Card>
            <CardHeader
                title={t(cardTitle)}
                subheader={isString(validationErrors) ? validationErrors : ""}
                subheaderTypographyProps={{ color: isString(validationErrors) ? "error" : '' }}
            />
            {
                nationalAddressRequired && <>
                    <Divider />
                    {
                        (isSuccess || !!error) &&
                        <CardContent>
                            {isSuccess && <Typography color="green.main">{t("Done successfully")}</Typography>}
                            {!!error && <Typography color="error">{t(error)}</Typography>}
                        </CardContent>
                    }
                    <CardContent>
                        <Grid container spacing={3} justifyContent="space-between">
                            <Grid item xs={8} md={10}>
                                <TextField
                                    fullWidth
                                    label={t("Short Address Code")}
                                    placeholder={t("Example (RRRR1234)")}
                                    size="small"
                                    name="nationalAddress"
                                    value={nationalAddress}
                                    onChange={(event) => {
                                        const value = sanitizeShortAddress(event.target.value);

                                        setValidateNationalNumber(null);
                                        setNationalAddress(value);

                                        handleChange({
                                            target: {
                                                name: "address",
                                                value: {
                                                    ...address,
                                                    shortAddress: value,
                                                },
                                            },
                                        });
                                    }}
                                    error={!!shortAddressError}
                                    helperText={!!shortAddressError ? t(shortAddressError) : ""}
                                />
                                {
                                    (!!address?.shortAddress && address?.shortAddress !== nationalAddress) &&
                                    <Typography variant="subtitle2" color="warning.main" sx={{ mt: 1 }}>
                                        {t("If you chanege Short Address Code, please click Search to auto fill the address fields.")}
                                    </Typography>
                                }
                            </Grid>
                            <Grid item xs={4} md={2} sx={{ textAlign: "end" }}>
                                <LoadingButton endIcon={<SearchIcon />} onClick={getAddressFromNationalAddress} loading={loading} sx={{ textTransform: 'none' }} variant='contained' disabled={disableNationalAddress}>
                                    {t("Search")}
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </CardContent>
                </>
            }
            <Divider />
            <CardContent>
                <Grid container spacing={3}>
                    {
                        Fields.map((field, index) => (
                            <Grid item xs={field.xs} md={field.md} key={field?.name}>
                                {
                                    field?.name === "city" ?
                                        <MapitShipCitiesAutocomplete
                                            name={field?.name}
                                            value={get(address, field?.name)}
                                            onChange={handleChange}
                                            error={get(validationErrors, field?.name)}
                                            required={field?.required}
                                        />
                                        :
                                        <MapItTextField
                                            label={t(field?.label)}
                                            size={field?.size}
                                            name={field?.name}
                                            value={get(address, field?.name) ?? ""}
                                            handleChange={handleChange}
                                            error={get(validationErrors, field?.name)}
                                            inputProps={{ maxLength: field?.maxLength }}
                                            placeholder={!!field?.placeholder ? `${t(field?.placeholder)} ${field?.required && '*'}` : undefined}
                                            required={field?.required}
                                        />
                                }
                            </Grid>
                        ))
                    }
                </Grid>
            </CardContent>
        </Card>
    )
}
